/**
 * The shell: rail, bottom bar, routes — behind one question, "who is this?".
 *
 * The navigation lists all ten screens from `docs/SCREENS.md`, and four of them are not built —
 * phases 3 and 4. They appear, marked, rather than being hidden. Hiding them would make the
 * product look complete and leave a reviewer to discover the gap from a sales conversation; naming
 * them makes the scope of this build legible from the first screen.
 *
 * The question is asked of `/v1/sso/session` before anything else renders. A 401 — from that call
 * or from any later one, which `api()` broadcasts — swaps the whole shell for the sign-in screen.
 * Before this gate existed a production visitor saw the rail, every screen, and eleven copies of
 * "Not authenticated." in eleven fonts; the product had no front door, only walls with the error
 * message painted on them. Any other failure to answer stays an error in the API's own words,
 * because "the session service is down" and "you are not signed in" send a person to different
 * places.
 */

import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { DELIVERED_THROUGH_PHASE, MOBILE_TABS, NAV } from "@hub/core/browser";
import { ApiError, UNAUTHENTICATED_EVENT, useApi } from "./lib/api";
import { Capabilities } from "./screens/Capabilities";
import { Dashboard } from "./screens/Dashboard";
import { Identities } from "./screens/Identities";
import { Access } from "./screens/Access";
import { Agents } from "./screens/Agents";
import { AgentDetail } from "./screens/AgentDetail";
import { Graph } from "./screens/Graph";
import { Threats } from "./screens/Threats";
import { Tools } from "./screens/Tools";
import { Assurance } from "./screens/Assurance";
import { NotBuiltYet } from "./screens/NotBuiltYet";
import { Sso } from "./screens/Sso";
import { SignIn } from "./screens/SignIn";

/** What `/v1/sso/session` answers. `via` is how the caller was admitted; the shell says so. */
interface Session {
  signedIn: boolean;
  tenantId: string;
  identityId: string;
  displayName: string;
  email: string;
  amr: string;
  via: "session" | "dev-header";
}

/** Where sign-out lands: the hub's own sign-in page, so the next visit starts at the front door. */
const SIGN_OUT_HREF = "/v1/sso/logout?next=%2Fv1%2Fsso%2Fsignin";

export function App() {
  const session = useApi<Session>("/v1/sso/session");
  const [refused, setRefused] = useState<ApiError | null>(null);

  useEffect(() => {
    const onRefused = (e: Event) => setRefused((e as CustomEvent<ApiError>).detail);
    window.addEventListener(UNAUTHENTICATED_EVENT, onRefused);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onRefused);
  }, []);

  if (session.loading) return <p className="empty">Checking who you are…</p>;
  const unauthenticated = refused ?? (session.error?.status === 401 ? session.error : null);
  if (unauthenticated) return <SignIn reason={unauthenticated.message} hint={unauthenticated.hint} />;
  if (session.error) {
    return <p className="err">{session.error.message}{session.error.hint ? ` — ${session.error.hint}` : ""}</p>;
  }
  return <Shell who={session.data} />;
}

function Shell({ who }: { who: Session | null }) {
  // The alerts badge counts open critical and warning items. It reads the dashboard rather than a
  // dedicated endpoint so the badge and the screen can never disagree about the same number.
  const { data } = useApi<{ needsDecision: { anomalies: number; approvals: number } }>("/v1/dashboard");
  const alerts = (data?.needsDecision.anomalies ?? 0);

  return (
    <div className="app">
      <aside className="rail">
        <div className="brand">Security Package Hub</div>
        <nav aria-label="Sections">
          {NAV.map((n) => (
            <NavLink key={n.route} to={n.route} end={n.route === "/"}>
              <span>{n.label}</span>
              {n.route === "/alerts" && alerts > 0 && <span className="pill bad">{alerts}</span>}
              {n.phase > DELIVERED_THROUGH_PHASE && <span className="pill dim">Phase {n.phase}</span>}
            </NavLink>
          ))}
        </nav>
        {who && <Who who={who} />}
      </aside>

      <main className="main">
        <Routes>
          <Route path="/" element={<Capabilities />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/identities" element={<Identities />} />
          <Route path="/sso" element={<Sso />} />
          <Route path="/access" element={<Access />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/agents/:id" element={<AgentDetail />} />
          <Route path="/identities/:id" element={<AgentDetail />} />
          <Route path="/graph" element={<Graph />} />
          <Route path="/threats" element={<Threats />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/assurance" element={<Assurance />} />
          {/* Phases 3 and 4. Named, with what they will do and what exists today, rather than a
              404 that reads as a broken link or a stub that reads as a working feature. */}
          <Route path="/reviews" element={<NotBuiltYet route="/reviews" />} />
          <Route path="/onboard" element={<NotBuiltYet route="/onboard" />} />
          <Route path="/policies" element={<NotBuiltYet route="/policies" />} />
          <Route path="/alerts" element={<NotBuiltYet route="/alerts" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <nav className="bottom-bar" aria-label="Sections">
        {MOBILE_TABS.map((route) => {
          const entry = NAV.find((n) => n.route === route);
          return (
            <NavLink key={route} to={route} end={route === "/"}>
              {entry?.short ?? route}
            </NavLink>
          );
        })}
        {/* The rail is hidden at phone width; sign-out must still be reachable there. */}
        {who && <a href={SIGN_OUT_HREF} data-testid="sign-out-m">Sign out</a>}
      </nav>
    </div>
  );
}

/**
 * Who is signed in, and how. The development identity header is LABELLED as such rather than
 * shown as a person: a reviewer who typed "operator" into localStorage must never read it back as
 * somebody who signed in — the same rule as the mock banner on a screen fed by a mock.
 */
function Who({ who }: { who: Session }) {
  return (
    <div className="who" data-testid="who">
      <div className="who-name">{who.displayName}</div>
      {who.email && <div className="who-email">{who.email}</div>}
      {who.via === "dev-header"
        ? <span className="pill dim" data-testid="who-dev">dev identity header</span>
        : <span className="pill ok">signed in</span>}
      <a href={SIGN_OUT_HREF} data-testid="sign-out">Sign out</a>
    </div>
  );
}
